/**
 * A synthesised throat, for grunts and barks. One syllable is a buzzy source (a saw, standing in
 * for the glottis) through two formant band-passes, with a little breath on top. It says nothing
 * in any language (07 §3) - only pitch contour, vowel colour and rhythm carry the meaning.
 */

import type { Layer } from '../synth/patch';

/** First and second formants, Hz, of a few neutral vowels. */
export const VOWEL = {
  ah: [730, 1090],
  uh: [640, 1190],
  eh: [530, 1840],
  ee: [300, 2250],
  oh: [570, 840],
  oo: [320, 870],
} as const satisfies Record<string, readonly [number, number]>;

export interface Syllable {
  at: number;
  /** Seconds of voiced sound. */
  dur: number;
  /** Pitch contour, Hz: the whole meaning of a bark is whether this rises or falls. */
  from: number;
  to: number;
  vowel: keyof typeof VOWEL;
  gain?: number;
  attack?: number;
  /** Vibrato depth in semitones: strain, a long shout. */
  vib?: number;
  /** Breath noise level, 0..1. */
  breath?: number;
}

/** The layers of one syllable. */
export function syllable(s: Syllable): Layer[] {
  const [f1, f2] = VOWEL[s.vowel];
  const gain = s.gain ?? 1;
  const attack = s.attack ?? 0.012;
  const hold = Math.max(0, s.dur * 0.55 - attack);
  const decay = Math.max(0.02, s.dur - attack - hold);
  const tone = (formant: number, q: number, level: number): Layer => {
    const layer: Layer = {
      wave: 'saw', at: s.at, freq: s.from, freqEnd: s.to, attack, hold, decay, curve: 1.5, gain: gain * level,
      filter: { type: 'bp', cutoff: formant, q },
    };
    if (s.vib !== undefined) {
      layer.vibHz = 6.5;
      layer.vibDepth = s.vib;
    }
    return layer;
  };
  const layers = [tone(f1, 5, 1), tone(f2, 7, 0.7)];
  const breath = s.breath ?? 0.12;
  if (breath > 0) {
    layers.push({
      wave: 'noise', at: s.at, attack, hold, decay, curve: 1.5, gain: gain * breath,
      filter: { type: 'bp', cutoff: (f1 + f2) / 2, q: 1.2 },
    });
  }
  return layers;
}

/** Several syllables in a row. */
export function utterance(syllables: Syllable[]): Layer[] {
  return syllables.flatMap(syllable);
}
